import { create } from 'zustand';
import { setMoneyFractionDigits, setMoneyLocale } from '@/shared/types/Money';
import { setLocale } from '@/shared/i18n';

export type AuthUser = {
  id: string;
  email: string;
  full_name: string;
  roles: string[];
  company_id: string;
  default_branch_id?: string | null;
  // Hard branch assignment: when set, the user is locked to this branch on
  // every document (auto-filled, read-only, mandatory).
  assigned_branch_id?: string | null;
  // Set when an admin issued the current password: the API refuses every call
  // except /auth/me, /auth/change-password and /auth/logout until it is changed.
  must_change_password?: boolean;
  // Installable apps this membership may open (rep-app program W1a):
  // 'wms_rf' and/or 'sales_rep'. The ERP itself is implicit and never listed.
  apps?: string[];
};

export type CompanyInfo = {
  id: string;
  code: string;
  name: string;
  functional_currency_code: string;
  secondary_currency_code?: string | null;
  multi_currency_enabled: boolean;
  date_format?: string;
  round_invoice_totals?: boolean;
  // Company-wide over-receipt tolerance as a FRACTION ('0.0500' = 5%); the
  // last step of the item > vendor > company cascade in receiving.
  over_receipt_tolerance?: string | number | null;
  // Marked as a demo by the platform login: what prints carries the demo watermark.
  is_demo?: boolean;
  // Height (px) of the company logo on printouts. Default 48.
  print_logo_height?: number;
  // Decimal places for displaying money/cost amounts. Default 2.
  decimal_places?: number;
  // UI language / locale (e.g. 'en', 'es'). Default 'en'.
  language?: string;
  // The company tracks stock in bins (WMS) rather than in locations only
  // (settings.inventory.use_bins). Read it through useUseBins().
  use_bins?: boolean;
  // Open lines of new (not yet confirmed) sales orders reduce available stock
  // too (settings.inventory.new_orders_reduce_available). Default true.
  new_orders_reduce_available?: boolean;
  // A sales rep's availability counts only their branch's warehouses and the
  // warehouses with no branch (settings.inventory.rep_branch_stock_only).
  // Default false; office users always see every warehouse.
  rep_branch_stock_only?: boolean;
  // Every rep sees every customer in the Sales app, not only the customers
  // assigned to them (settings.rep_app.rep_sees_all_customers). Default false.
  rep_sees_all_customers?: boolean;
  // The company has a QuickBooks Online connection that is not disconnected
  // (connected or needing a reconnect): Receivables > QuickBooks and the
  // QuickBooks badge on AR invoices show only then.
  quickbooks_connected?: boolean;
  // What the Sales app's Catalog opens on (settings.rep_app.catalog_mode):
  // the category tiles ('categories', the default) or the sales catalogs'
  // tiles ('catalogs', Sales → Sales catalogs).
  catalog_mode?: 'categories' | 'catalogs';
  // Company contact details shown in the printout header.
  details?: CompanyDetails;
  // Bank / payment details shown in the invoice's payment block.
  payment?: CompanyPayment;
};

export type CompanyDetails = {
  address?: string;
  phone?: string;
  fax?: string;
  email?: string;
  website?: string;
  contact_name?: string;
  tax_id?: string;
  registration_no?: string;
};

export type CompanyPayment = {
  bank_name?: string;
  account_name?: string;
  account_number?: string;
  iban?: string;
  swift?: string;
  routing?: string;
  instructions?: string;
};

export type CompanyMembership = {
  id: string;
  code: string;
  name: string;
  roles: string[];
};

type AuthState = {
  user: AuthUser | null;
  company: CompanyInfo | null;
  companies: CompanyMembership[];
  ready: boolean;
  setSession: (u: AuthUser | null, c: CompanyInfo | null) => void;
  setCompanies: (list: CompanyMembership[]) => void;
  setReady: (v: boolean) => void;
  clear: () => void;
  hasRole: (role: string) => boolean;
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  company: null,
  companies: [],
  ready: false,
  setSession: (user, company) => { setMoneyFractionDigits(company?.decimal_places ?? 2); setMoneyLocale(company?.functional_currency_code, company?.language); setLocale(company?.language ?? 'en'); set({ user, company }); },
  setCompanies: (companies) => set({ companies }),
  setReady: (v) => set({ ready: v }),
  clear: () => set({ user: null, company: null, companies: [] }),
  hasRole: (role) => !!get().user?.roles?.includes(role),
}));
